/**
 * SPLH Dashboard Data Service V2
 *
 * Handles data fetching for the SPLH Performance Dashboard with comparison support.
 * Provides GraphQL query execution with client-side caching.
 *
 * Architecture:
 * - Sends clientId, dateRange, and comparisonMode to backend
 * - Backend handles ALL calculations and aggregations
 * - Frontend receives ready-to-render comparison-aware data
 * - Client-side caching with 5-minute TTL to prevent redundant requests
 * - Parallel AI insights fetching (non-blocking)
 *
 * @module pages/common/dashboards/SPLHDashboard/services/splhDataServiceV2
 */

import { API, graphqlOperation } from 'aws-amplify';
import * as logger from '../../../../../utils/logger';
import { getSPLHDashboardDataV2 as getSPLHDashboardDataV2Query, generateSplhAiInsights as generateSplhAiInsightsQuery } from './splhQueriesV2';

/**
 * Comparison mode options
 * @constant {Object}
 */
export const COMPARISON_MODES = {
  PREVIOUS_PERIOD: 'PREVIOUS_PERIOD',
  PREVIOUS_MONTH: 'PREVIOUS_MONTH',
  PREVIOUS_YEAR: 'PREVIOUS_YEAR',
};

/**
 * Default comparison mode
 * @constant {string}
 */
export const DEFAULT_COMPARISON_MODE = COMPARISON_MODES.PREVIOUS_PERIOD;

/**
 * Cache configuration
 * @constant {number}
 */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * In-memory cache for dashboard data
 * Key format: `${clientId}:${startDate}:${endDate}:${comparisonMode}`
 * @type {Map<string, { data: Object, timestamp: number }>}
 */
const dashboardCacheV2 = new Map();

/**
 * In-memory cache for AI insights
 * Key format: `${clientId}:${startDate}:${endDate}:${comparisonMode}`
 * @type {Map<string, { data: Object, timestamp: number }>}
 */
const aiInsightsCache = new Map();

/**
 * Generates a cache key from query parameters
 *
 * @param {string} clientId - Client identifier
 * @param {Object} dateRange - Date range object
 * @param {string} comparisonMode - Comparison mode
 * @returns {string} Cache key
 * @private
 */
function getCacheKey(clientId, dateRange, comparisonMode) {
  return `${clientId}:${dateRange.startDate}:${dateRange.endDate}:${comparisonMode}`;
}

/**
 * Checks if a cached entry is still valid
 *
 * @param {{ data: Object, timestamp: number }} cacheEntry - Cached entry
 * @returns {boolean} True if cache is still valid
 * @private
 */
function isCacheValid(cacheEntry) {
  if (!cacheEntry) return false;
  const age = Date.now() - cacheEntry.timestamp;
  return age < CACHE_TTL_MS;
}

/**
 * Extracts user-friendly error message from GraphQL errors
 *
 * @param {Error|Object} error - Error object from GraphQL response
 * @returns {string} User-friendly error message
 * @private
 */
function extractErrorMessage(error) {
  if (error?.errors?.length > 0) {
    const firstError = error.errors[0];
    return firstError.message || 'An error occurred while fetching dashboard data';
  }

  if (error?.message) {
    if (error.message.includes('Network') || error.message.includes('network')) {
      return 'Unable to connect to the server. Please check your connection and try again.';
    }
    if (error.message.includes('timeout') || error.message.includes('Timeout')) {
      return 'The request timed out. Please try again.';
    }
    if (error.message.includes('unauthorized') || error.message.includes('Unauthorized')) {
      return 'You are not authorized to view this dashboard. Please sign in again.';
    }
    return 'Unable to load dashboard data. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Validates the input parameters for the dashboard query
 *
 * @param {string} clientId - Client identifier
 * @param {Object} dateRange - Date range object
 * @param {string} comparisonMode - Comparison mode
 * @returns {{ isValid: boolean, error: string|null }} Validation result
 * @private
 */
function validateInput(clientId, dateRange, comparisonMode) {
  if (!clientId || typeof clientId !== 'string') {
    return { isValid: false, error: 'Client ID is required' };
  }

  if (!dateRange || typeof dateRange !== 'object') {
    return { isValid: false, error: 'Date range is required' };
  }

  if (!dateRange.startDate || !dateRange.endDate) {
    return { isValid: false, error: 'Start date and end date are required' };
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateRange.startDate) || !dateRegex.test(dateRange.endDate)) {
    return { isValid: false, error: 'Dates must be in YYYY-MM-DD format' };
  }

  if (dateRange.startDate > dateRange.endDate) {
    return { isValid: false, error: 'Start date must be before or equal to end date' };
  }

  const validModes = Object.values(COMPARISON_MODES);
  if (!validModes.includes(comparisonMode)) {
    return { isValid: false, error: `Invalid comparison mode. Must be one of: ${validModes.join(', ')}` };
  }

  return { isValid: true, error: null };
}

/**
 * Fetches SPLH Dashboard data V2 from the backend with comparison support
 *
 * This function:
 * - Validates input parameters
 * - Checks cache for existing valid data
 * - Executes GraphQL query if cache miss
 * - Caches successful responses
 * - Returns ready-to-render comparison-aware data from backend
 *
 * @param {string} clientId - Client identifier (e.g., 'dem50')
 * @param {Object} dateRange - Date range for the query
 * @param {string} dateRange.startDate - Start date in YYYY-MM-DD format
 * @param {string} dateRange.endDate - End date in YYYY-MM-DD format
 * @param {string} [comparisonMode='PREVIOUS_PERIOD'] - Comparison mode
 * @param {Object} [options={}] - Optional configuration
 * @param {boolean} [options.skipCache=false] - Skip cache and force fresh fetch
 * @returns {Promise<Object>} Dashboard data with comparison-aware structures
 * @throws {Error} If validation fails or the query fails
 *
 * @example
 * const data = await fetchSPLHDashboardDataV2('dem50', {
 *   startDate: '2025-11-18',
 *   endDate: '2025-11-24'
 * }, 'PREVIOUS_PERIOD');
 */
export async function fetchSPLHDashboardDataV2(clientId, dateRange, comparisonMode = DEFAULT_COMPARISON_MODE, options = {}) {
  const { skipCache = false } = options;

  const validation = validateInput(clientId, dateRange, comparisonMode);
  if (!validation.isValid) {
    logger.warn('Invalid input for fetchSPLHDashboardDataV2', {
      clientId,
      dateRange,
      comparisonMode,
      error: validation.error,
    });
    throw new Error(validation.error);
  }

  const cacheKey = getCacheKey(clientId, dateRange, comparisonMode);

  if (!skipCache) {
    const cached = dashboardCacheV2.get(cacheKey);
    if (isCacheValid(cached)) {
      logger.debug('Returning cached SPLH dashboard V2 data', {
        clientId,
        dateRange,
        comparisonMode,
        cacheAge: Date.now() - cached.timestamp,
      });
      return cached.data;
    }
  }

  logger.debug('Fetching SPLH dashboard V2 data', { clientId, dateRange, comparisonMode });

  try {
    const response = await API.graphql(
      graphqlOperation(getSPLHDashboardDataV2Query, {
        input: {
          clientId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          comparisonMode,
        },
      })
    );

    const rawData = response?.data?.getSPLHDashboardDataV2;

    if (!rawData) {
      logger.warn('Empty response from getSPLHDashboardDataV2', { clientId, dateRange, comparisonMode });
      throw new Error('No data returned from server');
    }

    // Transform hierarchyMetadata.levels to use expected field names
    // Schema uses levelId/label, but components expect level/displayName
    const data = {
      ...rawData,
      hierarchyMetadata: rawData.hierarchyMetadata ? {
        ...rawData.hierarchyMetadata,
        levels: rawData.hierarchyMetadata.levels?.map(level => ({
          level: level.levelId,
          displayName: level.label,
          order: level.order,
          isUsed: level.isUsed,
        })) || [],
      } : null,
    };

    dashboardCacheV2.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    logger.debug('Successfully fetched SPLH dashboard V2 data', {
      clientId,
      dateRange,
      comparisonMode,
      hasKpis: Boolean(data.kpis?.length),
      trendDataPoints: data.trendData?.length || 0,
      hierarchyNodes: data.hierarchyData?.length || 0,
    });

    return data;
  } catch (error) {
    if (error.message === 'No data returned from server') {
      throw error;
    }

    const userMessage = extractErrorMessage(error);

    logger.error('Failed to fetch SPLH dashboard V2 data', {
      clientId,
      dateRange,
      comparisonMode,
      error: error?.message || error,
      graphqlErrors: error?.errors,
    });

    throw new Error(userMessage);
  }
}

/**
 * Fetches AI insights using pre-aggregated data from dashboard response
 *
 * This should be called in parallel (non-blocking) after receiving dashboard data.
 * Uses aiInput from the dashboard response to minimize payload size.
 *
 * @param {Object} aiInput - Pre-aggregated AI input from dashboard response
 * @param {Object} [options={}] - Optional configuration
 * @param {boolean} [options.skipCache=false] - Skip cache and force fresh fetch
 * @returns {Promise<Object>} AI insights response
 *
 * @example
 * // Get dashboard data first
 * const dashboardData = await fetchSPLHDashboardDataV2(clientId, dateRange, mode);
 *
 * // Then fetch AI insights in parallel (non-blocking)
 * const aiInsights = await fetchAIInsights(dashboardData.aiInput);
 */
export async function fetchAIInsights(aiInput, options = {}) {
  const { skipCache = false } = options;

  if (!aiInput?.clientId) {
    logger.warn('Missing AI input for fetchAIInsights');
    return {
      summary: '',
      available: false,
      errorMessage: 'AI input not available',
      generatedAt: new Date().toISOString(),
    };
  }

  const cacheKey = `${aiInput.clientId}:${aiInput.currentRange?.startDate}:${aiInput.currentRange?.endDate}:${aiInput.comparisonMode}`;

  if (!skipCache) {
    const cached = aiInsightsCache.get(cacheKey);
    if (isCacheValid(cached)) {
      logger.debug('Returning cached AI insights', { cacheKey });
      return cached.data;
    }
  }

  logger.debug('Fetching AI insights', { clientId: aiInput.clientId });

  try {
    const response = await API.graphql(
      graphqlOperation(generateSplhAiInsightsQuery, {
        input: {
          clientId: aiInput.clientId,
          comparisonMode: aiInput.comparisonMode,
          currentRange: aiInput.currentRange,
          comparisonRange: aiInput.comparisonRange,
          kpiSummary: aiInput.kpiSummary,
          alertsSummary: aiInput.alertsSummary,
          topPerformers: aiInput.topPerformers,
          bottomPerformers: aiInput.bottomPerformers,
        },
      })
    );

    const data = response?.data?.generateSplhAiInsights || {
      summary: '',
      available: false,
      errorMessage: 'No response from AI service',
      generatedAt: new Date().toISOString(),
    };

    aiInsightsCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    logger.debug('Successfully fetched AI insights', {
      clientId: aiInput.clientId,
      available: data.available,
      summaryLength: data.summary?.length || 0,
    });

    return data;
  } catch (error) {
    logger.error('Failed to fetch AI insights', {
      clientId: aiInput.clientId,
      error: error?.message || error,
    });

    return {
      summary: '',
      available: false,
      errorMessage: 'AI insights are temporarily unavailable. Please try again later.',
      generatedAt: new Date().toISOString(),
    };
  }
}

/**
 * Clears the dashboard V2 cache
 *
 * @param {string} [clientId] - Optional client ID to clear only that client's cache
 * @returns {void}
 */
export function clearCacheV2(clientId) {
  if (clientId) {
    for (const key of dashboardCacheV2.keys()) {
      if (key.startsWith(`${clientId}:`)) {
        dashboardCacheV2.delete(key);
      }
    }
    for (const key of aiInsightsCache.keys()) {
      if (key.startsWith(`${clientId}:`)) {
        aiInsightsCache.delete(key);
      }
    }
    logger.debug('Cleared SPLH dashboard V2 cache for client', { clientId });
  } else {
    dashboardCacheV2.clear();
    aiInsightsCache.clear();
    logger.debug('Cleared all SPLH dashboard V2 cache');
  }
}

/**
 * Gets cache statistics for debugging
 *
 * @returns {{ dashboardSize: number, aiSize: number }} Cache statistics
 */
export function getCacheStatsV2() {
  return {
    dashboardSize: dashboardCacheV2.size,
    aiSize: aiInsightsCache.size,
  };
}
